import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from '../components/ui/use-toast';
import { Tag, X } from 'lucide-react';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });
  const [shippingSettings, setShippingSettings] = useState(null);

  useEffect(() => {
    loadSettings();
    loadShippingSettings();
    checkAffiliateCode();
  }, []);

  const checkAffiliateCode = () => {
    // Check URL for affiliate code
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setAffiliateCode(refCode);
      sessionStorage.setItem('affiliate_code', refCode);
    } else {
      // Check session storage
      const savedCode = sessionStorage.getItem('affiliate_code');
      if (savedCode) {
        setAffiliateCode(savedCode);
      }
    }
  };

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadShippingSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-options`);
      if (response.ok) {
        const data = await response.json();
        setShippingSettings(data);
      }
    } catch (error) {
      console.error('Failed to load shipping settings:', error);
    }
  };

  // Check if all items in cart qualify for free shipping
  const checkAllItemsHaveFreeShipping = () => {
    if (!shippingSettings || !cartItems.length) return false;
    
    const freeShippingProducts = shippingSettings.free_shipping_products || [];
    const freeShippingCollections = shippingSettings.free_shipping_collections || [];
    
    // If no products are marked for free shipping, return false
    if (freeShippingProducts.length === 0 && freeShippingCollections.length === 0) {
      return false;
    }
    
    // Check if ALL items in cart have free shipping
    return cartItems.every(item => {
      const productId = item.id || item._id;
      
      // Check if product is in free shipping list
      if (freeShippingProducts.includes(productId)) return true;
      
      // Check if product's category is in free shipping collections
      if (item.category && freeShippingCollections.includes(item.category)) return true;
      
      return false;
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setValidatingCoupon(true);
    try {
      const subtotal = getCartTotal();
      const result = await api.validateCoupon(couponCode, subtotal);
      setAppliedCoupon(result);
      toast({
        title: 'Coupon applied!',
        description: `You saved $${result.discount_amount.toFixed(2)}`
      });
    } catch (error) {
      toast({
        title: 'Invalid coupon',
        description: error.response?.data?.detail || 'This coupon cannot be applied',
        variant: 'destructive'
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const subtotal = getCartTotal();
      const shippingRate = settings?.shipping?.flat_rate || 10;
      const freeShippingThreshold = settings?.shipping?.free_shipping_threshold;
      
      // Calculate shipping cost - check product-specific free shipping first
      const allItemsFreeShipping = checkAllItemsHaveFreeShipping();
      let shippingCost = shippingRate;
      
      if (allItemsFreeShipping) {
        shippingCost = 0;
      } else if (freeShippingThreshold && subtotal >= freeShippingThreshold) {
        shippingCost = 0;
      }
      
      // Calculate discount
      const discountAmount = appliedCoupon?.discount_amount || 0;
      
      // Calculate total
      const total = subtotal + shippingCost - discountAmount;
      
      // Process items - upload design images first if needed
      const processedItems = await Promise.all(cartItems.map(async (item) => {
        let designImageUrl = null;
        
        // Check multiple possible image sources
        const possibleDesignImage = item.customization?.designImage || 
                                    item.customization?.image ||
                                    item.image;
        
        console.log('[Checkout] Processing item:', item.name, {
          hasCustomization: !!item.customization,
          designImage: possibleDesignImage?.substring?.(0, 50) || 'none',
          imageType: possibleDesignImage?.startsWith?.('data:') ? 'base64' : 'url'
        });
        
        // Send the design as-is (base64 or DB-backed URL); the backend validates it and embeds base64 into the order
        designImageUrl = possibleDesignImage || null;
        
        return {
          product_id: item.id || item._id,
          product_name: item.name,
          price: item.price,
          quantity: item.quantity,
          customization: item.customization ? {
            from: item.customization.from,
            to: item.customization.to,
            templateId: item.customization.templateId,
            designImage: designImageUrl,
            arEnabled: item.customization.arEnabled,
            arGreetingId: item.customization.arGreetingId,
            arQrCode: item.customization.arQrCode,
            arCharge: item.customization.arCharge,
            // Oreo cookie customization
            type: item.customization.type,
            useSameImage: item.customization.useSameImage,
            cookieImages: item.customization.cookieImages,
            cookieCount: item.customization.cookieCount
          } : null
        };
      }));
      
      // Prepare checkout data for Stripe
      const checkoutData = {
        origin_url: window.location.origin,
        customer_email: formData.email,
        items: processedItems,
        shipping_cost: shippingCost,
        discount_amount: discountAmount,
        coupon_code: appliedCoupon?.code || null,
        affiliate_code: affiliateCode || null,
        shipping_address: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode
        }
      };
      
      // Create Stripe checkout session
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: 'Error',
        description: 'Failed to proceed to payment. Please try again.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    navigate('/edible-greetings/cart');
    return null;
  }

  const subtotal = getCartTotal();
  const shippingRate = settings?.shipping?.flat_rate || 10;
  const freeShippingThreshold = settings?.shipping?.free_shipping_threshold;
  
  // Calculate shipping cost - check product-specific free shipping first
  const allItemsFreeShipping = checkAllItemsHaveFreeShipping();
  let shippingCost = shippingRate;
  
  if (allItemsFreeShipping) {
    shippingCost = 0;
  } else if (freeShippingThreshold && subtotal >= freeShippingThreshold) {
    shippingCost = 0;
  }
  
  const discountAmount = appliedCoupon?.discount_amount || 0;
  const total = subtotal + shippingCost - discountAmount;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-medium text-gray-900 mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <Card className="p-6">
                <h2 className="text-xl font-medium text-gray-900 mb-6">Contact Information</h2>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>
              </Card>

              {/* Shipping Address */}
              <Card className="p-6">
                <h2 className="text-xl font-medium text-gray-900 mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Payment Information */}
              <Card className="p-6">
                <h2 className="text-xl font-medium text-gray-900 mb-6">Payment Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cardNumber">Card Number</Label>
                    <Input
                      id="cardNumber"
                      name="cardNumber"
                      placeholder="1234 5678 9012 3456"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="expiryDate">Expiry Date</Label>
                      <Input
                        id="expiryDate"
                        name="expiryDate"
                        placeholder="MM/YY"
                        value={formData.expiryDate}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cvv">CVV</Label>
                      <Input
                        id="cvv"
                        name="cvv"
                        placeholder="123"
                        value={formData.cvv}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24">
                <h2 className="text-xl font-medium text-gray-900 mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  {cartItems.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="text-gray-900">{item.name}</p>
                        <p className="text-gray-600">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Coupon Code */}
                <div className="mb-6 border-t border-gray-200 pt-4">
                  <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Have a coupon code?
                  </Label>
                  {!appliedCoupon ? (
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Enter code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={validatingCoupon}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyCoupon}
                        disabled={!couponCode.trim() || validatingCoupon}
                      >
                        {validatingCoupon ? 'Applying...' : 'Apply'}
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded p-2">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">{appliedCoupon.code}</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="text-green-600 hover:text-green-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className={shippingCost === 0 ? 'text-green-600 font-medium' : ''}>
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  {allItemsFreeShipping && shippingCost === 0 && (
                    <p className="text-xs text-green-600">
                      ✓ Free shipping on all items in your cart!
                    </p>
                  )}
                  {!allItemsFreeShipping && freeShippingThreshold && subtotal < freeShippingThreshold && shippingCost > 0 && (
                    <p className="text-xs text-gray-500">
                      Add ${(freeShippingThreshold - subtotal).toFixed(2)} more for free shipping!
                    </p>
                  )}
                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-medium text-gray-900 text-lg">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full mt-6 py-6 text-lg"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Place Order'}
                </Button>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
